import { getArticleContent } from '../../services/products/articles';
import { applicationSettings } from '../../settings/application';
import geometry from '../../scene/util/geometry';
import tacHelpers from '../../state/tac/tacHelpers';
import constants from '../../settings/constants';
import { isRealArticleProduct, isType } from '../../services/products';
import { TacItem } from '../../generalTypes';
import { TacModel } from '../../state/tac/tacTypes';
import { getPlannerVersion } from '../plannerVersion';

interface ICFVector3 {
  x: number;
  y: number;
  z: number;
}

interface ICFArticle {
  id: number;
  parent_ids: number[];
  child_ids: number[];
  product_id: string;
  name: string;
  category: string;
  colors: string[];
  attributes: Record<string, unknown>;
  dimensions: ICFVector3;
  transform: {
    position: ICFVector3;
    rotation: ICFVector3;
    scale: ICFVector3;
  };
}

interface ICFArticleDimensionsAndTransform {
  dimensions: ICFVector3;
  transform: {
    position: ICFVector3;
    rotation: ICFVector3;
    scale: ICFVector3;
  };
}

interface ICFModel {
  icf_version: string;
  drawing_id?: string;
  vpc_code?: string;
  application_id: string;
  application_version: string | undefined;
  creation_datetime: string;
  articles: ICFArticle[];
  metadata?: Record<string, unknown>;
}

function getFormattedDateTime(): string {
  // '%Y-%m-%dT%H:%M:%SZ', '2021-01-01T01:01:01Z'
  return new Date().toISOString();
}

function getArticleId(internalId: string): string {
  const article = getArticleContent(internalId);
  return article
    ? `${article.itemType}-${article.itemNoGlobal || article.itemNoLocal}`
    : '';
}

function getColorHexCode(item: TacItem): string {
  return constants.COLOR_CODES[item.filter.color];
}

function isValidICFItem(item: TacItem): boolean {
  return isRealArticleProduct(item);
}

function getICFArticleDimensionsAndTransform(
  item: TacItem,
  parent: TacItem | null,
  tac: TacModel
): ICFArticleDimensionsAndTransform {
  const { depth, height, width } = item;

  //Not loving this but can't come up with anything better atm.
  const isSidePanel = isType(item, 'side-panel');
  const actualDepth = isSidePanel && parent ? parent.depth : depth;

  const dimensions = {
    x: width,
    y: height,
    z: actualDepth,
  };

  const globalCoords = tacHelpers.getGlobalCoords(
    { ...item, depth: actualDepth },
    tac
  );
  const centeredCoords = geometry.getCenter(globalCoords);
  const { x, y, z } = centeredCoords;

  return {
    dimensions,
    transform: {
      position: {
        x,
        y,
        z,
      },
      rotation: {
        x: 0,
        y: 0,
        z: 0,
      },
      scale: { x: 1, y: 1, z: 1 },
    },
  };
}

function getICFArticlesFromTacItem(
  item: TacItem,
  parent: TacItem | null,
  tac: TacModel
): ICFArticle[] {
  const { filter, itemid, items: children, name, iows, sprno } = item;

  const articleIds = [
    ...(sprno ? [sprno] : iows.map(iowsItem => iowsItem.itemno)),
  ];

  const parentIsValid = parent && isValidICFItem(parent);

  // @ts-ignore
  return articleIds.map(id => ({
    id: itemid,
    parent_ids: [...(parent && parentIsValid ? [parent.itemid] : [])],
    child_ids: [
      ...(children
        ? children.filter(isValidICFItem).map((child: TacItem) => child.itemid)
        : []),
    ],
    product_id: getArticleId(id),
    name,
    category: filter.type,
    colors: [getColorHexCode(item)],
    attributes: {},
    ...getICFArticleDimensionsAndTransform(item, parent, tac),
  }));
}

function getICFArticlesFromTacItemHierarchy(
  item: TacItem,
  parent: TacItem | null,
  tac: TacModel
): ICFArticle[] {
  const { items: children } = item;

  const itemIsValid = isValidICFItem(item);

  return [
    ...(itemIsValid ? getICFArticlesFromTacItem(item, parent, tac) : []),
    ...(children
      ? children.flatMap((child: TacItem) =>
          getICFArticlesFromTacItemHierarchy(child, item, tac)
        )
      : []),
  ];
}

function getICFArticlesFromTac(tac: TacModel): ICFArticle[] {
  return tac.items.flatMap((item: TacItem) =>
    getICFArticlesFromTacItemHierarchy(item, null, tac)
  );
}

export function getICF(tac: TacModel): ICFModel {
  return {
    icf_version: '1.0',
    application_id: applicationSettings.applicationName.toLowerCase(),
    application_version: getPlannerVersion(),
    creation_datetime: getFormattedDateTime(),
    articles: getICFArticlesFromTac(tac),
  };
}
